import { useEffect } from 'react';
import { X } from 'lucide-react';

const PRIVACY_CONTENT = [
  {
    title: '1. Who We Are',
    body: 'This Privacy Policy explains how Tolvai Company LLC ("Tolvai," "we," "us," or "our") collects, uses, shares, and protects personal information when you visit tolvai.com or use our products and services (collectively, the "Services").',
  },
  {
    title: '2. Information We Collect',
    subsections: [
      {
        label: 'Information you provide to us',
        list: [
          'Contact information submitted through a form, email, or support request',
          'Feedback or other information you choose to share with us',
        ],
      },
      {
        label: 'Information we collect automatically',
        list: [
          'Device and usage information (browser type, operating system, pages viewed, timestamps)',
          'IP address and approximate location derived from it',
          'Cookies and similar technologies (see Section 4)',
        ],
      },
    ],
  },
  {
    title: '3. How We Use Your Information',
    prefix: 'We use personal information to:',
    list: [
      'Provide, operate, secure, and improve the Services',
      'Communicate with you, including for support and service-related notices',
      'Detect and prevent fraud and abuse',
      'Comply with our legal obligations',
      'Establish, exercise, or defend legal claims',
    ],
  },
  {
    title: '4. Cookies and Similar Technologies',
    body: 'We and our service providers may use cookies and similar technologies to operate the Services, remember your preferences, and understand how the Services are used. You can control cookie settings through your browser at any time.',
  },
  {
    title: '5. How We Share Your Information',
    prefix: 'We do not sell your personal information. We may share it with:',
    list: [
      'Service providers who perform services on our behalf (e.g., hosting, analytics), under contracts that limit their use of your information',
      'Professional advisors, such as lawyers and auditors',
      'Regulators or law enforcement where required by law or to protect our rights',
      'A successor entity in connection with a merger, acquisition, or sale of assets',
      'Other parties with your consent',
    ],
  },
  {
    title: '6. Data Retention',
    body: 'We retain personal information for as long as necessary to provide the Services, fulfill the purposes described in this Policy, and comply with our legal obligations, after which it is deleted or anonymized.',
  },
  {
    title: '7. Your Privacy Rights',
    prefix: 'Depending on your location, you may have the right to:',
    list: [
      'Access the personal information we hold about you',
      'Correct inaccurate or incomplete information',
      'Request deletion of your information',
      'Object to or request that we restrict certain processing',
      'Receive a copy of your information in a portable format',
      'Withdraw consent where processing is based on consent',
    ],
    suffix: 'To exercise any of these rights, contact us using the details in Section 10.',
  },
  {
    title: '8. Security',
    body: 'We use administrative, technical, and physical safeguards designed to protect personal information. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
  },
  {
    title: '9. Children\'s Privacy',
    body: 'The Services are not directed to children under 13, and we do not knowingly collect personal information from them. If we learn we have done so, we will delete it promptly.',
  },
  {
    title: '10. Third-Party Links',
    body: 'The Services may link to third-party websites or services. This Policy does not apply to those third parties, and we encourage you to review their privacy notices.',
  },
  {
    title: '11. Changes to This Policy',
    body: 'We may update this Policy from time to time. Material changes will be notified through the Services or by other reasonable means, and the "Last Updated" date will reflect the most recent revision.',
  },
  {
    title: '12. Contact Us',
    body: 'You can reach us at legal@tolvai.com.',
  },
];

export default function PrivacyModal({ isOpen, onClose }) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="Terms-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Privacy Policy">
      <div className="Terms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="Terms-modal-header">
          <div>
            <span className="Terms-modal-eyebrow">Tolvai Company LLC</span>
            <h2 className="Terms-modal-title">Privacy Policy</h2>
            <p className="Terms-modal-date">Effective: October, 2026</p>
          </div>
          <button className="Terms-close" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="Terms-modal-body">
          {PRIVACY_CONTENT.map((section) => (
            <div key={section.title} className="Terms-section">
              <h3 className="Terms-section-title">{section.title}</h3>

              {section.subsections && section.subsections.map((sub) => (
                <div key={sub.label} className="Terms-subsection">
                  <p className="Terms-subsection-label">{sub.label}</p>
                  <ul className="Terms-section-list">
                    {sub.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}

              {section.prefix && <p className="Terms-section-text">{section.prefix}</p>}
              {section.list && (
                <ul className="Terms-section-list">
                  {section.list.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
              {section.suffix && <p className="Terms-section-text">{section.suffix}</p>}
              {section.body && <p className="Terms-section-text">{section.body}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}